import Cookies from "js-cookie";
import jsPDF from "jspdf";
import PropTypes from "prop-types";
import axios from "axios";
import styled from "styled-components";
import {useCallback, useEffect, useRef, useState} from "react";
import {axiosInstance} from "../../utilities/AxiosConfig";
import {Sidebar} from "primereact/sidebar";
import {Button} from 'primereact/button';

import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import {notify} from "../toast";
import {useSpinner} from "../../context/SpinnerContext";

// Define styled components for styling
const WebcamContainer = styled.div`
    position: relative;
    width: 100%;
    max-width: 400px;
    margin: 0 auto;
`;

const WebcamVideo = styled.video`
    width: 100%;
    border-radius: 10px;
`;

const PreviewImg = styled.img`
    width: 100%;
    border-radius: 10px;
`;

const PreviewPdf = styled.iframe`
    width: 100%;
    height: 500px;
    border-radius: 10px;
    border: none;
`;

const WebcamCanvas = styled.canvas`
    display: none; /* Hide canvas by default */
`;

const WebcamButton = styled.button`
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    background-color: #fff;
    color: #333;
    border: none;
    border-radius: 20px;
    padding: 10px 20px;
    font-size: 16px;
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

export const Scanner = (props) => {
    const [imageName, setImageName] = useState([]);

    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [level, setLevel] = useState(10);
    const [mediaStream, setMediaStream] = useState(null);
    const [capturedImage, setCapturedImage] = useState(null);
    const [rawData, setRawData] = useState([]);
    const [id, setId] = useState(props.invoice.toString(10).padStart(5, '0'));
    const [refresh, setRefresh] = useState(false);
    const [uploading, setUploading] = useState(true);
    const [position, setPosition] = useState(-1);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        const url = `image/${props.mode}-${id}`;
        axiosInstance.get(url).then((response) => {
            if (response.data.length > 0) {
                setRawData(response.data.map((item) => item.image));
                setImageName(response.data.map((item) => item.name));
            } else {
                setImageName([]);
                setRawData([]);
            }
        }).catch((error) => {
            notify(error.message, "error");
        }).finally(() => {
            setLoading(false);
        });

    }, [refresh]);

    const startWebcam = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: true,
            });
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.width = 640;
                videoRef.current.height = 200;
            }
            setMediaStream(stream);
        } catch (error) {
            notify("Error accessing webcam", "error");
        }
    };

    // Function to stop the webcam
    const stopWebcam = () => {
        if (mediaStream) {
            mediaStream.getTracks().forEach((track) => {
                track.stop();
            });
            setMediaStream(null);
        }
    };

    const handleImageClick = useCallback((index) => {
        setCapturedImage(rawData[index]);
        setPosition(index);
        setUploading(false);
    }, [rawData]);

    const captureImage = () => {
        if (videoRef.current && canvasRef.current) {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            const context = canvas.getContext("2d");
            if (context && video.videoWidth && video.videoHeight) {
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                context.drawImage(video, 0, 0, canvas.width, canvas.height);
                const imageDataUrl = canvas.toDataURL("image/jpeg");
                setCapturedImage(imageDataUrl);
            }
            setUploading(true);
            stopWebcam();
        }
    };

    const upload = () => {
        const invoiceNumberString = props.invoice.toString(10).padStart(5, '0');
        const nameString = props.mode + "-" + invoiceNumberString;
        props.setLoading(true);
        axiosInstance.get(`image/number/${nameString}`).then(response => {
            if (response.data < 20) {
                const formData = new FormData();
                const fileName = nameString + "-" + response.data.toString() + ".jpg";
                const blobImage = base64ToBlob(capturedImage);
                formData.append('image', blobImage, fileName);
                axiosInstance.post(`image/`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    }
                }).then(response => {
                    if (response.status === 200) {
                        notify(`image ${response.data} uploaded successfully`, "success");
                        props.setShowImage(false);
                    } else {
                        notify(">>> Error uploading image", "error");
                    }
                }).catch(error => {
                    notify(error.message, "error");
                });
                setRefresh(!refresh);
            }

        }).catch(error => {
            notify(error.message, "error");
        }).finally(() => {
            props.setLoading(false);
            resetState();
        });
    };

    const resetState = () => {
        stopWebcam(); // Stop the webcam if it's active
        setPosition(-1);
        setCapturedImage(null); // Reset captured image
    };

    const removeImage = () => {
        const nameString = imageName[position];
        props.setLoading(true);
        axiosInstance.delete(`image/${nameString}`).then(response => {
            if (response.status === 200) {
                notify(`image ${response.data} deleted successfully`, "success");
                setRefresh(!refresh);
            } else {
                notify(">>> Error deleting image", "error");
            }
        }).catch(error => {
            notify(error.message, "error");
        }).finally(() => {
                props.setLoading(false);
                resetState();
            }
        );
    };

    const isPdfDataUrl = (value) => typeof value === "string" && value.startsWith("data:application/pdf");

    const printPdfDataUrl = (pdfDataUrl, title = "document") => {
        const pdfBlob = base64ToBlob(pdfDataUrl, "application/pdf");
        const blobUrl = URL.createObjectURL(pdfBlob);

        const printWindow = window.open(blobUrl, "_blank", "noopener,noreferrer");
        if (!printWindow) {
            notify("Popup blocked. Please allow popups to print.", "error");
            URL.revokeObjectURL(blobUrl);
            return;
        }

        printWindow.onload = () => {
            try {
                printWindow.document.title = title;
            } catch (e) {
                // ignore
            }
            printWindow.focus();
            printWindow.print();
            setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
        };
    };

    const handlePrint = () => {
        if (!capturedImage || position === -1) return;

        const title = imageName[position] || "document";

        if (isPdfDataUrl(capturedImage)) {
            printPdfDataUrl(capturedImage, title);
            return;
        }

        if (isImageDataUrl(capturedImage)) {
            printImageDataUrl(capturedImage, title);
            return;
        }

        notify("Unsupported file format for printing.", "error");
    };

    const customHeader = (
        <div className="flex flex-row justify-content-between align-items-center gap-2 mr-1">
            <Button icon="pi pi-file-arrow-up" className="mr-1" onClick={upload}
                    disabled={!capturedImage || !uploading}/>
            <Button icon="pi pi-trash" className="mr-1" onClick={removeImage}
                    disabled={!capturedImage || uploading || position === -1}/>
            <Button icon="pi pi-print" className="mr-1" onClick={handlePrint}
                    disabled={!capturedImage || uploading || position === -1}/>
        </div>
    );

    if (!loading) {
        return (
            <div className="card flex justify-content-center">
                <Sidebar header={customHeader} visible={props.showImage} onHide={() => props.setShowImage(false)}>
                    <WebcamContainer>
                        {capturedImage ? (
                            <>
                                {isPdfDataUrl(capturedImage) ? (
                                    <PreviewPdf src={capturedImage} title="PDF Preview"/>
                                ) : (
                                    <PreviewImg src={capturedImage} className="captured-image"/>
                                )}
                                <WebcamButton onClick={resetState}>Reset</WebcamButton>
                            </>
                        ) : (
                            <>
                                <WebcamVideo ref={videoRef} autoPlay muted/>
                                <WebcamCanvas ref={canvasRef}/>
                                {!videoRef.current || !videoRef.current.width ? (
                                    <WebcamButton onClick={startWebcam}>Start Webcam</WebcamButton>
                                ) : (
                                    <WebcamButton onClick={captureImage}>Capture Image</WebcamButton>
                                )}
                            </>
                        )}
                    </WebcamContainer>

                    {rawData.length > 0 &&
                        rawData.map((item, index) => {
                            const isPdf = isPdfDataUrl(item);
                            return isPdf ? (
                                <div
                                    key={index}
                                    className="m-3 p-2 border-1 surface-border border-round cursor-pointer flex align-items-center justify-content-center"
                                    style={{width: '100%', height: '100px', backgroundColor: '#f8f9fa'}}
                                    onClick={() => handleImageClick(index)}
                                >
                                    <i className="pi pi-file-pdf text-4xl text-red-500 mr-2"></i>
                                    <span>{imageName[index] || "PDF Document"}</span>
                                </div>
                            ) : (
                                <PreviewImg
                                    key={index}
                                    src={item}
                                    className="captured-image m-3"
                                    onClick={() => handleImageClick(index)}
                                    onError={(e) => {
                                        e.currentTarget.onerror = null;
                                        e.currentTarget.src = 'fallback-image-url';
                                    }}
                                />
                            );
                        })
                    }
                </Sidebar>
            </div>
        );
    }
};

Scanner.propTypes = {
    loading: PropTypes.bool.isRequired,
    setLoading: PropTypes.func.isRequired,
    invoice: PropTypes.number.isRequired,
    mode: PropTypes.string.isRequired,
    setShowImage: PropTypes.func.isRequired,
    showImage: PropTypes.bool.isRequired,
};

function base64ToBlob(base64String, mimeType = '') {
    const parts = base64String.split(';');
    const contentType = parts[0].split(':')[1] || mimeType;
    const raw = window.atob(parts[1].split(',')[1]);
    const rawLength = raw.length;
    const uint8Array = new Uint8Array(rawLength);

    for (let i = 0; i < rawLength; ++i) {
        uint8Array[i] = raw.charCodeAt(i);
    }

    return new Blob([uint8Array], {type: contentType});
}

const isPdfDataUrl = (value) => typeof value === "string" && value.startsWith("data:application/pdf");
const isImageDataUrl = (value) => typeof value === "string" && value.startsWith("data:image/");

const printImageDataUrl = (imageDataUrl, title = "image") => {
    // Convert data URL -> Blob URL (more reliable than printing data: directly)
    const imgBlob = base64ToBlob(imageDataUrl);
    const blobUrl = URL.createObjectURL(imgBlob);

    // Create hidden iframe (no popup)
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.style.opacity = "0";
    iframe.setAttribute("aria-hidden", "true");

    document.body.appendChild(iframe);

    const cleanup = () => {
        try {
            URL.revokeObjectURL(blobUrl);
        } catch (e) {
            // ignore
        }
        try {
            document.body.removeChild(iframe);
        } catch (e) {
            // ignore
        }
    };

    const doc = iframe.contentWindow?.document;
    if (!doc) {
        cleanup();
        notify("Unable to open print frame.", "error");
        return;
    }

    const safeTitle = String(title).replaceAll("<", "&lt;").replaceAll(">", "&gt;");

    doc.open();
    doc.write(`
      <!doctype html>
      <html>
        <head>
          <title>${safeTitle}</title>
          <style>
            @page { size: auto; margin: 10mm; }
            html, body { height: 100%; margin: 0; padding: 0; }
            .page { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; }
            img { object-fit: contain; max-width: 100%; max-height: 100%; display: block; }
          </style>
        </head>
        <body>
          <div class="page">
            <img id="printImg" src="${blobUrl}" alt="print" />
          </div>
          <script>
            const img = document.getElementById('printImg');
            img.onload = () => {
              window.focus();
              window.print();
            };
          </script>
        </body>
      </html>
    `);
    doc.close();

    // Cleanup after printing (timing varies by browser)
    iframe.contentWindow.onafterprint = () => {
        cleanup();
    };

    // Fallback cleanup in case onafterprint doesn't fire
    setTimeout(cleanup, 60_000);
};


export const ViewImages = (props) => {
    const [imageName, setImageName] = useState([]);
    const [loading, setLoading] = useState(false);

    const [capturedImage, setCapturedImage] = useState(null);
    const [rawData, setRawData] = useState([]);
    const [id, setId] = useState(props.invoice.toString(10).padStart(5, '0'));
    const [refresh, setRefresh] = useState(false);
    const [position, setPosition] = useState(-1);

    useEffect(() => {
        setLoading(true);
        const url = `image/${props.mode}-${id}`;
        axiosInstance.get(url).then((response) => {
            if (response.data.length > 0) {
                setImageName(response.data.map((item) => item.name));
                setRawData(response.data.map((item) => item.image));
            } else {
                setImageName([]);
                setRawData([]);
            }
        }).catch((error) => {
            notify(error.message, "error");
        }).finally(() => {
            setLoading(false);
        });

    }, [refresh, setRefresh]);

    const handleImageClick = useCallback((index) => {
        setCapturedImage(rawData[index]);
        setPosition(index);

    }, [rawData]);

    const isPdfDataUrl = (value) => typeof value === "string" && value.startsWith("data:application/pdf");

    const printPdfDataUrl = (pdfDataUrl, title = "document") => {
        const pdfBlob = base64ToBlob(pdfDataUrl, "application/pdf");
        const blobUrl = URL.createObjectURL(pdfBlob);

        const printWindow = window.open(blobUrl, "_blank", "noopener,noreferrer");
        if (!printWindow) {
            notify("Popup blocked. Please allow popups to print.", "error");
            URL.revokeObjectURL(blobUrl);
            return;
        }

        printWindow.onload = () => {
            try {
                printWindow.document.title = title;
            } catch (e) {
                // ignore
            }
            printWindow.focus();
            printWindow.print();
            setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
        };
    };

    const handlePrint = () => {
        if (!capturedImage || position === -1) return;

        const title = imageName[position] || "document";

        if (isPdfDataUrl(capturedImage)) {
            printPdfDataUrl(capturedImage, title);
            return;
        }

        if (isImageDataUrl(capturedImage)) {
            printImageDataUrl(capturedImage, title);
            return;
        }

        notify("Unsupported file format for printing.", "error");
    };
    const customHeader = (
        <Button icon="pi pi-print" className="mr-1" onClick={handlePrint}
                disabled={!capturedImage || position === -1}/>
    );

    if (!loading) {
        return (
            <div className="card flex justify-content-center">
                <Sidebar header={customHeader} visible={props.showImage} onHide={() => props.setShowImage(false)}>
                    <WebcamContainer>
                        {capturedImage ? (
                            <>
                                {isPdfDataUrl(capturedImage) ? (
                                    <PreviewPdf src={capturedImage} title="PDF Preview"/>
                                ) : (
                                    <PreviewImg src={capturedImage} className="captured-image"/>
                                )}
                            </>
                        ) : (
                            <>
                                <div className="card flex flex-column flex-fill w-full h-full justify-content-center align-content-center">
                                    No Images!!!
                                </div>
                            </>
                        )}
                    </WebcamContainer>

                    {rawData.length > 0 ?
                        rawData.map((item, index) => {
                            const isPdf = isPdfDataUrl(item);
                            return isPdf ? (
                                <div
                                    key={index}
                                    className="m-3 p-2 border-1 surface-border border-round cursor-pointer flex align-items-center justify-content-center"
                                    style={{width: '100%', height: '100px', backgroundColor: '#f8f9fa'}}
                                    onClick={() => handleImageClick(index)}
                                >
                                    <i className="pi pi-file-pdf text-4xl text-red-500 mr-2"></i>
                                    <span>{imageName[index] || "PDF Document"}</span>
                                </div>
                            ) : (
                                <PreviewImg
                                    key={index}
                                    src={item}
                                    className="captured-image  m-3"
                                    onClick={() => handleImageClick(index)}
                                    onError={(e) => {
                                        e.currentTarget.onerror = null; // Prevents infinite loop
                                        e.currentTarget.src = 'fallback-image-url'; // Optional fallback
                                    }}
                                />
                            );
                        }) :
                        <div>No Images!!!</div>
                    }
                </Sidebar>
            </div>
        );
    }
};

ViewImages.propTypes = {
    invoice: PropTypes.number.isRequired,
    mode: PropTypes.string.isRequired,
    setShowImage: PropTypes.func.isRequired,
    showImage: PropTypes.bool.isRequired,
};